import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Veuillez entrer votre adresse email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      // API always returns success to prevent email enumeration
      // but we still handle potential network errors
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white border-slate-200 shadow-lg">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <CardTitle className="text-2xl font-semibold text-slate-900">
              Email envoyé
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-600">
              Si l'adresse <strong>{email}</strong> est associée à un compte, 
              vous recevrez un lien de réinitialisation dans quelques instants.
            </p>
            <p className="text-sm text-slate-500">
              Le lien est valable pendant 60 minutes.
            </p>
            <div className="pt-4 space-y-3">
              <Button
                variant="outline"
                onClick={() => setSent(false)}
                className="w-full btn-outline"
              >
                Renvoyer un email
              </Button>
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full text-slate-600">
                  <ArrowLeft size={16} className="mr-2" />
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white border-slate-200 shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-16 h-16 bg-[#26788A] rounded-full flex items-center justify-center mb-4">
            <Mail size={32} className="text-white" />
          </div>
          <CardTitle className="text-2xl font-semibold text-slate-900">
            Mot de passe oublié ?
          </CardTitle>
          <p className="text-slate-500 text-sm">
            Entrez votre adresse email pour recevoir un lien de réinitialisation.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Adresse email
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field mt-2"
                placeholder="votre@email.fr"
                required
                autoFocus
                data-testid="forgot-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3"
              data-testid="forgot-submit-btn"
            >
              {loading ? "Envoi en cours..." : "Envoyer le lien de réinitialisation"}
            </Button>
            <Link to="/login" className="block">
              <Button variant="ghost" type="button" className="w-full text-slate-600">
                <ArrowLeft size={16} className="mr-2" />
                Retour à la connexion
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
